import React, { useEffect, useState } from 'react';
import { camerasAPI } from '../../utils/api';
import { formatPresenceDuration as formatDuration, getPresenceTodayDate, PresenceSummaryZone } from '../../utils/presence';
import PresenceStatus from './PresenceStatus';
import '../DetectionsStatsModal/DetectionsStatsModal.css';
import './PresenceStatsModal.css';

interface PresenceStatsCamera {
  id: number | null;
  camera_id: string | null;
  name: string;
}

interface CameraSummary {
  key: string;
  name: string;
  zones: PresenceSummaryZone[];
  error?: string;
}

interface Props {
  cameras: PresenceStatsCamera[];
  areaName: string;
  onClose: () => void;
}

const PresenceStatsModal: React.FC<Props> = ({ cameras, areaName, onClose }) => {
  const [date, setDate] = useState(getPresenceTodayDate);
  const [summaries, setSummaries] = useState<CameraSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setSummaries([]);
    if (!date) { setLoading(false); return; }
    setLoading(true);
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      const results = await Promise.all(cameras.map(async (camera): Promise<CameraSummary> => {
        const base = { key: String(camera.id ?? camera.camera_id ?? camera.name), name: camera.name, zones: [] };
        if (!camera.camera_id) return { ...base, error: 'Камера ещё не синхронизирована' };
        try {
          const response = await camerasAPI.getPresenceSummary(camera.camera_id, date);
          if (!response.success || !response.data) throw new Error(response.message || 'Не удалось загрузить итог');
          return { ...base, zones: response.data.zones };
        } catch (error) {
          return { ...base, error: error instanceof Error ? error.message : 'Ошибка загрузки статистики' };
        }
      }));
      if (!cancelled) { setSummaries(results); setLoading(false); }
      inFlight = false;
    };
    void load();
    const timer = window.setInterval(() => {
      if (date === getPresenceTodayDate() && document.visibilityState === 'visible') void load();
    }, 10_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [cameras, date, refresh]);

  const successful = summaries.filter((summary) => !summary.error);
  const absences = successful.reduce((total, camera) => total + camera.zones.reduce((sum, zone) => sum + zone.absences, 0), 0);
  const duration = successful.reduce((total, camera) => total + camera.zones.reduce((sum, zone) => sum + zone.total_absence_sec, 0), 0);

  return <div className="area-modal det-stats-modal" role="dialog" aria-modal="true" aria-labelledby="presence-stats-title">
    <div className="area-modal__backdrop" onClick={onClose} />
    <div className="area-modal__dialog det-stats-modal__dialog">
      <header className="area-modal__header">
        <div><h2 id="presence-stats-title" className="area-modal__title">Статистика присутствия</h2><p className="det-stats__subtitle">{areaName} · камеры с контролем присутствия</p></div>
        <button type="button" className="area-modal__close" aria-label="Закрыть" onClick={onClose}>×</button>
      </header>
      <div className="det-stats-modal__body">
        <div className="presence-stats__filters">
          <label>Дата <input className="ui-input" type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <button type="button" className="ui-btn ui-btn--outline ui-btn--sm" disabled={loading || !date} onClick={() => setRefresh((value) => value + 1)}>Обновить</button>
        </div>
        <p className="det-stats__subtitle">Дневные итоги и текущий статус рабочих мест. Повторные снимки не считаются отдельными периодами.{date === getPresenceTodayDate() && ' Обновляется каждые 10 секунд.'}</p>
        {loading ? <div className="det-stats__loading" role="status">Загрузка статистики…</div> : !date ? <div className="det-stats__empty">Выберите дату</div> : <>
          {successful.length > 0 && <div className="det-stats__cards presence-stats__totals">
            <div className="det-stats__card det-stats__card--day"><span className="det-stats__card-label">Периодов отсутствия</span><span className="det-stats__card-value">{absences}</span></div>
            <div className="det-stats__card"><span className="det-stats__card-label">Суммарно по рабочим местам</span><span className="presence-stats__duration">{formatDuration(duration)}</span></div>
          </div>}
          {summaries.some((camera) => camera.error) && <p className="det-stats__error" role="alert">Не все камеры доступны. Итоги учитывают только загруженные данные.</p>}
          {summaries.length === 0 && <div className="det-stats__empty">На участке нет камер с контролем присутствия</div>}
          {summaries.map((camera) => <section className="det-stats__section" key={camera.key}>
            <h3>{camera.name}</h3>
            {camera.error ? <p className="det-stats__error">{camera.error}</p> : camera.zones.length === 0 ? <p className="det-stats__empty">Нет данных за выбранный день</p> : <div className="ui-table-wrap"><table className="ui-table presence-stats__table">
              <thead><tr><th>Рабочее место</th><th>Статус сейчас</th><th>Отсутствий</th><th>Общее отсутствие</th></tr></thead>
              <tbody>{camera.zones.map((zone, index) => <tr key={index}><td>{zone.zone_name ?? 'Весь кадр'}</td><td><PresenceStatus zone={zone} /></td><td>{zone.absences}</td><td>{formatDuration(zone.total_absence_sec)}</td></tr>)}</tbody>
            </table></div>}
          </section>)}
        </>}
      </div>
      <footer className="area-modal__footer"><button type="button" className="area-modal__submit area-modal__submit--secondary" onClick={onClose}>Закрыть</button></footer>
    </div>
  </div>;
};

export default PresenceStatsModal;
